import { memo, ReactNode } from 'react';
import ChevronButton from 'src/components/Buttons/ChevronButton';
import Collapsible from 'src/components/Collapsible/Collapsible';
import { cm } from 'src/utils/classNames';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';

interface MobileTableCardRootProps extends WithClassName, WithChildren {
	/**
	 * If passed, forces component to use `Collapsible`.
	 * Only necessary if you're using `CollapsibleHeader` and `CollapsibleContent`.
	 */
	expanded?: boolean;
	/**
	 * Only necessary if you're using `CollapsibleHeader` and `CollapsibleContent`.
	 * If passed, should also pass a value for `expanded`.
	 */
	setExpanded?: (expanded: boolean) => void;
}

function MobileTableCardRoot({
	expanded,
	setExpanded,
	children,
	className,
}: MobileTableCardRootProps) {
	const { Component, props } = (() => {
		if (expanded === undefined) {
			return { Component: 'div', props: {} };
		}

		return {
			Component: Collapsible.Root,
			props: { open: expanded, onOpenChange: setExpanded },
		};
	})();

	return (
		<Component
			className={cm(
				'flex flex-col rounded-sm bg-container-bg p-md text-text-default',
				'[&_[data-collapsible-card-fade]]:transition-opacity [&_[data-collapsible-card-fade]]:duration-300',
				expanded && '[&_[data-collapsible-card-fade]]:opacity-0',
				className
			)}
			{...props}
		>
			{children}
		</Component>
	);
}

interface MobileTableCardCollapsibleHeaderProps extends WithChildren {
	expanded: boolean;
}

function MobileTableCardCollapsibleHeader({
	children,
	expanded,
}: MobileTableCardCollapsibleHeaderProps) {
	return (
		<Collapsible.Trigger className="flex items-center gap-x-md" asChild>
			<div>
				{children}
				<ChevronButton direction={expanded ? 'up' : 'down'} />
			</div>
		</Collapsible.Trigger>
	);
}

function MobileTableCardCollapsibleContent({ children }: WithChildren) {
	return (
		<Collapsible.Content className="mt-lg">{children}</Collapsible.Content>
	);
}

function MobileTableCardLabel({
	className,
	children,
}: WithClassName<WithChildren>) {
	return (
		<span
			className={cm(
				'flex items-center gap-x-xs typo-body-xs whitespace-nowrap text-text-secondary',
				className
			)}
		>
			{children}
		</span>
	);
}

function MobileTableCardValue({
	className,
	children,
}: WithClassName<WithChildren>) {
	return (
		<div
			className={cm(
				'flex items-center gap-x-xs typo-label-xs text-text-default',
				className
			)}
		>
			{children}
		</div>
	);
}

interface MobileTableCardLabelWithValueProps extends WithClassName {
	label: ReactNode;
	value: ReactNode;
	labelClassName?: string;
	valueClassName?: string;
}

function MobileTableCardLabelWithValue({
	label,
	value,
	labelClassName,
	valueClassName,
	className,
}: MobileTableCardLabelWithValueProps) {
	return (
		<div className={cm('flex flex-col gap-y-xs', className)}>
			<MobileTableCardLabel className={labelClassName}>
				{label}
			</MobileTableCardLabel>
			<MobileTableCardValue className={valueClassName}>
				{value}
			</MobileTableCardValue>
		</div>
	);
}

function MobileTableCardEmptyPlaceholder({
	className,
	children,
}: WithClassName<WithChildren>) {
	return (
		<div
			className={cm(
				'flex min-h-40 items-center justify-center typo-label-sm text-text-secondary',
				className
			)}
		>
			{children}
		</div>
	);
}

export default {
	Root: memo(MobileTableCardRoot),
	CollapsibleHeader: memo(MobileTableCardCollapsibleHeader),
	CollapsibleContent: memo(MobileTableCardCollapsibleContent),
	Label: memo(MobileTableCardLabel),
	Value: memo(MobileTableCardValue),
	LabelWithValue: memo(MobileTableCardLabelWithValue),
	EmptyPlaceholder: memo(MobileTableCardEmptyPlaceholder),
};
